import React, { useMemo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import useDefer, { Status } from '../../common/hooks/useDefer'
import Text from '../../ui/Text'
import { Alert } from '../alert'
import usePatch from '../../common/hooks/usePatch'
import { SInput } from '../../support/features/charge-point-capi-details-widget/charge-point-capi-details-widget.style'
import { Button } from '../button'
import { createPartner as createPartnerRequest, ICreatePartnerForm } from '../create-partner'
import { validateEmailFormat } from '../validate-email-format'
import { validateHostNameFormat } from '../validate-hostname-format/validate-hostname-format'
import useText from '../../common/hooks/useText'

const SInputForm = styled.label`
    width: 100%;
    padding-bottom: 15px;
`

export type CreatePartnerModalProps = {
    onAddPartner: () => void
    close: () => void
    className?: ClassName
    style?: React.CSSProperties
}

export const CreatePartnerModal = React.memo(function CreatePartnerModal({
    onAddPartner,
    className,
    style,
    close,
}: CreatePartnerModalProps): React.ReactElement {
    const text = useText()
    const { value: partner, apply } = usePatch<ICreatePartnerForm>({
        name: '',
        description: '',
        email: '',
        phone: '',
        hostName: '',
    })

    const [saveError, setSaveError] = useState('')

    const isEmailValid = useMemo(() => (partner.email ? validateEmailFormat(partner.email) : true), [partner.email])
    const isHostNameValid = useMemo(
        () => (partner.hostName ? validateHostNameFormat(partner.hostName) : true),
        [partner.hostName]
    )

    const { execute: createPartner, status: partnerCreatingStatus } = useDefer(
        () =>
            createPartnerRequest(partner)
                .then(() => {
                    onAddPartner()
                    close()
                })
                .catch(() => setSaveError(text('msgPartnerCouldNotBeSaved'))),
        [partner]
    )

    return (
        <form
            className={cn(className)}
            style={style}
            onSubmit={(event) => {
                event.preventDefault()
                createPartner()
            }}
            noValidate
        >
            <div className="modal-header" data-testid="header">
                <Text code="lblNewPartnerModal" />
            </div>
            <div className="modal-body">
                {saveError && <Alert error message={saveError} />}
                <SInputForm>
                    <Text code="lblNameField" />
                    <SInput
                        value={partner.name}
                        onChange={(event) => apply({ name: event.target.value })}
                        data-testid="nameField"
                    />
                </SInputForm>
                <SInputForm>
                    <Text code="lblDescriptionField" />
                    <SInput
                        value={partner.description}
                        onChange={(event) => apply({ description: event.target.value })}
                        data-testid="descriptionField"
                    />
                </SInputForm>
                <SInputForm>
                    <Text code="lblEmailField" />
                    <SInput
                        value={partner.email}
                        onChange={(event) => apply({ email: event.target.value })}
                        data-testid="emailField"
                    />
                </SInputForm>
                <SInputForm>
                    <Text code="lblPhoneField" />
                    <SInput
                        value={partner.phone}
                        onChange={(event) => apply({ phone: event.target.value })}
                        data-testid="phoneField"
                    />
                </SInputForm>
                <SInputForm>
                    <Text code="lblHostNameField" />
                    <SInput
                        value={partner.hostName}
                        onChange={(event) => apply({ hostName: event.target.value })}
                        data-testid="hostNameField"
                        error={!isHostNameValid}
                    />
                    <span className="padding-top-15">
                        <b>
                            <Text code="lblExampleField" />
                        </b>
                        &nbsp;
                        <>my.charge.space</>
                    </span>
                </SInputForm>
            </div>
            <div className="modal-footer">
                <Button
                    type="submit"
                    disabled={
                        partnerCreatingStatus === Status.PENDING ||
                        !partner.description ||
                        !partner.name ||
                        !isHostNameValid ||
                        !partner.hostName ||
                        !isEmailValid
                    }
                    title={text('btnSave')}
                    loading={partnerCreatingStatus === Status.PENDING}
                    dataTestId="btn-save"
                />
                <Button onClick={close} title={text('btnCancel')} dataTestId="btn-cancel" />
            </div>
        </form>
    )
})
